import {
	BookOpen,
	ClipboardList,
	Clock,
	Folder,
	FolderPlus,
	MoreVertical,
	Pencil,
	Star,
	Trash2,
	Users,
} from 'lucide-react'
import { HEADER_HEIGHT } from '@/constants'
import type { Folder as FolderType } from '../../db/Types'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/DropdownMenu'
import logoIconLight from '../../assets/play-smith-icon-light.svg'
import logoIconDark from '../../assets/play-smith-icon-dark.svg'
import './content-sidebar.css'

export type SectionId =
	| 'playbooks'
	| 'documents'
	| 'recent'
	| 'shared'
	| 'starred'
	| 'trash';

interface SidebarProps {
	activeSection: SectionId
	onSectionChange: (section: SectionId) => void
	onSectionHover?: (section: SectionId) => void
	folders: FolderType[]
	isFoldersLoading?: boolean
	selectedFolderId: string | null
	onFolderSelect: (folderId: string | null) => void
	onAddFolder: () => void
	onRenameFolder: (folderId: string, currentName: string) => void
	onDeleteFolder: (folderId: string, name: string) => void
}

const contentTypeSections = [
	{ id: 'playbooks' as const, label: 'Playbooks', icon: BookOpen },
	{ id: 'documents' as const, label: 'Documents', icon: ClipboardList },
]

const filterSections = [
	{ id: 'recent' as const, label: 'Recent', icon: Clock },
	{ id: 'shared' as const, label: 'Shared with me', icon: Users },
	{ id: 'starred' as const, label: 'Starred', icon: Star },
	{ id: 'trash' as const, label: 'Trash', icon: Trash2 },
]

export function ContentSidebar({
								   activeSection,
								   onSectionChange,
								   onSectionHover,
								   folders,
								   isFoldersLoading = false,
								   selectedFolderId,
								   onFolderSelect,
								   onAddFolder,
								   onRenameFolder,
								   onDeleteFolder,
							   }: SidebarProps) {
	const handleFolderClick = (folderId: string) => {
		onFolderSelect(folderId)
	}

	return (
		<div className="content-sidebar" data-tour-id="content-sidebar">
			{/* Logo/Header */}
			<div className="content-sidebar-header" style={{ height: `${HEADER_HEIGHT}px` }}>
				<img
					src={logoIconDark}
					alt=""
					className="content-sidebar-logo content-sidebar-logo-dark"
				/>
				<img
					src={logoIconLight}
					alt=""
					className="content-sidebar-logo content-sidebar-logo-light"
				/>
				<h1 className="content-sidebar-title">Play Smith</h1>
			</div>

			{/* Navigation */}
			<nav className="content-sidebar-nav">
				{/* Content Types */}
				<div className="content-sidebar-group">
					<h3 className="content-sidebar-group-label">Content</h3>
					{contentTypeSections.map(section => (
						<button
							key={section.id}
							className={`content-sidebar-section ${activeSection === section.id && selectedFolderId === null ? 'content-sidebar-section-active' : ''}`}
							onClick={() => onSectionChange(section.id)}
							onMouseEnter={() => onSectionHover?.(section.id)}
						>
							<section.icon size={18} />
							<span>{section.label}</span>
						</button>
					))}
				</div>

				{/* Folders */}
				<div className="content-sidebar-group" data-tour-id="folder-tree">
					<div className="content-sidebar-group-header">
						<h3 className="content-sidebar-group-label">Folders</h3>
						<button
							className="content-sidebar-add-folder"
							onClick={onAddFolder}
							title="Add Folder"
						>
							<FolderPlus size={16} />
						</button>
					</div>
					<div className="content-sidebar-folders">
						{isFoldersLoading ? (
							<>
								{[1, 2, 3].map(i => (
									<div key={i} className="content-sidebar-folder-skeleton">
										<div className="content-sidebar-folder-skeleton-icon" />
										<div className="content-sidebar-folder-skeleton-text" />
									</div>
								))}
							</>
						) : folders.length === 0 ? (
							<div className="content-sidebar-folders-empty">
								No folders yet
							</div>
						) : null}
						{!isFoldersLoading && folders.map(folder => (
							<div
								key={folder.id}
								className={`content-sidebar-folder-wrapper ${selectedFolderId === folder.id ? 'content-sidebar-folder-active' : ''}`}
							>
								<button
									className="content-sidebar-folder"
									onClick={() => handleFolderClick(folder.id)}
								>
									<Folder size={16} />
									<span>{folder.name}</span>
									{folder.item_count != null && folder.item_count > 0 && (
										<span className="content-sidebar-folder-count">{folder.item_count}</span>
									)}
								</button>
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<button
											className="content-sidebar-folder-menu"
											onClick={(e) => e.stopPropagation()}
										>
											<MoreVertical size={14} />
										</button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										<DropdownMenuItem onClick={() => onRenameFolder(folder.id, folder.name)}>
											<Pencil size={14} />
											<span>Rename</span>
										</DropdownMenuItem>
										<DropdownMenuItem
											onClick={() => onDeleteFolder(folder.id, folder.name)}
											className="dropdown-menu-item-destructive"
										>
											<Trash2 size={14} />
											<span>Delete</span>
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						))}
					</div>
				</div>

				{/* Filters */}
				<div className="content-sidebar-group">
					<h3 className="content-sidebar-group-label">Filters</h3>
					{filterSections.map(section => (
						<button
							key={section.id}
							className={`content-sidebar-section ${activeSection === section.id && selectedFolderId === null ? 'content-sidebar-section-active' : ''}`}
							onClick={() => onSectionChange(section.id)}
							onMouseEnter={() => onSectionHover?.(section.id)}
						>
							<section.icon size={18} />
							<span>{section.label}</span>
						</button>
					))}
				</div>
			</nav>
		</div>
	)
}
